import React from 'react';
import { Info, Droplets, PaintBucket, Palette, ShieldCheck, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о красках и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о красках: виды, характеристики, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Droplets className="w-5 h-5 text-indigo-500" />
          Что нужно знать о краске?
        </h3>
        <p>
          Краска — это жидкий или порошкообразный материал, который наносится на поверхность тонким слоем и образует твердое покрытие при высыхании. Кроме декоративной функции, краска защищает поверхность от повреждений, влаги, ультрафиолета и других внешних воздействий.
        </p>

        <h3 className="flex items-center gap-2">
          <PaintBucket className="w-5 h-5 text-indigo-500" />
          Основные виды красок для внутренних работ
        </h3>
        <ul>
          <li>
            <strong>Водоэмульсионные краски</strong> — на водной основе, экологичные, быстросохнущие, практически без запаха. Отлично подходят для стен и потолков в жилых помещениях.
          </li>
          <li>
            <strong>Акриловые краски</strong> — разновидность водоэмульсионных красок с добавлением акриловых смол. Создают эластичное, устойчивое к растрескиванию и выцветанию покрытие. Хорошо моются.
          </li>
          <li>
            <strong>Латексные краски</strong> — водно-дисперсионные краски с добавлением латекса. Обладают повышенной влагостойкостью и стойкостью к истиранию. Идеальны для кухонь, ванных, детских комнат.
          </li>
          <li>
            <strong>Силиконовые краски</strong> — водоотталкивающие и паропроницаемые. "Дышащие" покрытия, устойчивые к загрязнениям и со способностью к самоочищению.
          </li>
          <li>
            <strong>Алкидные краски</strong> — на основе алкидных смол, растворенных в органических растворителях. Образуют прочное, устойчивое к влаге и температурным перепадам покрытие. Подходят для дерева и металла.
          </li>
          <li>
            <strong>Масляные краски</strong> — традиционный материал на основе олифы и пигментов. Имеют высокую укрывистость, но долго сохнут и имеют сильный запах.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-pink-500" />
          Характеристики красок
        </h3>
        <ul>
          <li><strong>Укрывистость</strong> — способность краски перекрывать цвет и фактуру окрашиваемой поверхности.</li>
          <li><strong>Расход</strong> — количество краски, необходимое для покрытия единицы площади. Указывается в литрах на квадратный метр (л/м²).</li>
          <li><strong>Степень блеска</strong> — от глубоко матовой до высокоглянцевой:
            <ul>
              <li><em>Матовая</em> — маскирует дефекты поверхности, но менее устойчива к загрязнениям.</li>
              <li><em>Полуматовая</em> — имеет легкий блеск, более устойчива к загрязнениям.</li>
              <li><em>Полуглянцевая</em> — отражает свет, хорошо моется.</li>
              <li><em>Глянцевая</em> — имеет зеркальный блеск, очень устойчива к загрязнениям и влаге.</li>
            </ul>
          </li>
          <li><strong>Влагостойкость</strong> — устойчивость к воздействию влаги и конденсата.</li>
          <li><strong>Стойкость к мытью</strong> — выдерживаемое количество циклов мытья без повреждения.</li>
          <li><strong>Время высыхания</strong> — период до полного застывания краски.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Как выбрать подходящую краску?</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            При выборе краски учитывайте назначение помещения, тип окрашиваемой поверхности, условия эксплуатации и желаемый визуальный эффект. Для жилых комнат предпочтительны экологичные водно-дисперсионные краски, для кухонь и ванных — влагостойкие моющиеся покрытия, для деревянных и металлических поверхностей — алкидные или специализированные краски.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-green-500" />
          Экологичность и безопасность красок
        </h3>
        <p>
          В России, как и во всем мире, растет внимание к экологичности отделочных материалов. Современные краски должны соответствовать требованиям безопасности, что подтверждается соответствующими сертификатами.
        </p>
        <ul>
          <li>
            <strong>Маркировка E1</strong> — означает низкое содержание летучих органических соединений (ЛОС).
          </li>
          <li>
            <strong>Знак "Листок жизни"</strong> — российский экологический сертификат, подтверждающий безопасность продукции.
          </li>
          <li>
            <strong>Маркировка "Безопасно для детей и беременных женщин"</strong> — указывает на отсутствие вредных веществ, допустимость использования в детских комнатах.
          </li>
        </ul>
        <p>
          Наиболее безопасными считаются водно-дисперсионные, акриловые и латексные краски с низким содержанием ЛОС. Масляные и алкидные краски содержат растворители и требуют более длительного проветривания помещения.
        </p>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Особенности окраски разных поверхностей
        </h3>
        <ul>
          <li>
            <strong>Гипсокартон</strong> — перед покраской требует грунтования для выравнивания впитывающей способности. Для швов необходима особая шпатлевка для гипсокартона.
          </li>
          <li>
            <strong>Штукатурка</strong> — новая штукатурка должна полностью просохнуть (до 28 дней для цементной). Требует предварительного грунтования.
          </li>
          <li>
            <strong>Дерево</strong> — нуждается в специальных грунтах, часто в предварительной обработке антисептиками. Требует тщательной шлифовки перед нанесением финишного покрытия.
          </li>
          <li>
            <strong>Металл</strong> — необходима антикоррозионная обработка, использование специальных грунтов и эмалей.
          </li>
          <li>
            <strong>Бетон</strong> — перед покраской требует полного высыхания (минимум 30 дней), грунтования и часто выравнивания шпатлевкой.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Распространенные ошибки при покраске
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Недостаточная подготовка поверхности (неровности, пыль, жир)</li>
            <li>Пропуск этапа грунтования, особенно на новых и сильно впитывающих поверхностях</li>
            <li>Покраска при неподходящей температуре и влажности (ниже +5°C или выше +30°C)</li>
            <li>Нанесение слишком толстого слоя, что приводит к подтекам и увеличивает время высыхания</li>
            <li>Использование некачественных инструментов, экономия на кистях и валиках</li>
          </ul>
        </div>

        <h3>Инструменты для покраски</h3>
        <ul>
          <li>
            <strong>Валики</strong> — основной инструмент для покраски больших площадей. Выбор валика зависит от типа краски и желаемой фактуры:
            <ul>
              <li>Валики с коротким ворсом (5-6 мм) — для гладких поверхностей и глянцевых красок</li>
              <li>Валики со средним ворсом (7-12 мм) — универсальные, для большинства стен и потолков</li>
              <li>Валики с длинным ворсом (от 13 мм) — для текстурированных поверхностей и фасадов</li>
            </ul>
          </li>
          <li>
            <strong>Кисти</strong> — необходимы для прокрашивания углов, стыков и других труднодоступных мест. Различаются по форме, размеру и типу щетины.
          </li>
          <li>
            <strong>Малярный скотч</strong> — для защиты поверхностей, которые не должны быть окрашены.
          </li>
          <li>
            <strong>Малярные ванночки и лотки</strong> — для удобного набора краски на валик или кисть.
          </li>
          <li>
            <strong>Телескопическая штанга</strong> — для покраски потолков и высоких стен без использования стремянки.
          </li>
        </ul>

        <h3>Основные этапы покраски</h3>
        <ol>
          <li><strong>Подготовка поверхности</strong> — очистка, заделка трещин и дефектов, шлифовка.</li>
          <li><strong>Грунтование</strong> — нанесение специального состава для улучшения адгезии краски к поверхности и снижения ее расхода.</li>
          <li><strong>Нанесение первого слоя краски</strong> — равномерно, тонким слоем, соблюдая технологию нанесения.</li>
          <li><strong>Высыхание</strong> — соблюдение времени высыхания согласно инструкции производителя.</li>
          <li><strong>Нанесение второго (финишного) слоя</strong> — для достижения равномерного покрытия и насыщенного цвета.</li>
        </ol>

        <h3>Тренды в окраске помещений</h3>
        <p>
          В России, как и во всем мире, наблюдаются определенные тенденции в выборе красок и цветовых решений:
        </p>
        <ul>
          <li><strong>Экологичность</strong> — растет популярность красок с низким содержанием вредных веществ.</li>
          <li><strong>Натуральные цвета</strong> — оттенки зеленого, терракоты, песка, древесины.</li>
          <li><strong>Матовые покрытия</strong> — создают глубину и благородство интерьера.</li>
          <li><strong>Колорблокинг</strong> — использование контрастных цветов для зонирования пространства.</li>
          <li><strong>Эффекты "бетон", "металлик", "перламутр"</strong> — специальные декоративные краски для создания уникальных поверхностей.</li>
        </ul>

        <p className="font-semibold mt-6">
          Правильный выбор краски и соблюдение технологии окрашивания помогут вам создать долговечное и привлекательное покрытие, которое будет радовать вас долгие годы!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;